import { useMemo } from 'react';
import Snowfall from 'react-snowfall';
import { useQuery } from '@tanstack/react-query';
import { getCompanySettings } from '@/lib/settings';

type Season = 'winter' | 'spring' | 'summer' | 'autumn';

function getSeason(): Season {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
}

function createPetalImages(): HTMLImageElement[] {
  const svgs = [
    // Sakura petal 1 — soft pink
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M16 2C12 2 8 8 8 14c0 4 3 8 8 14 5-6 8-10 8-14 0-6-4-12-8-12z" fill="#f9a8d4" opacity="0.7"/></svg>`,
    // Sakura petal 2 — lighter pink
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M16 4C13 4 10 9 10 14c0 3 2 7 6 12 4-5 6-9 6-12 0-5-3-10-6-10z" fill="#fbcfe8" opacity="0.6"/></svg>`,
    // Small blossom
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><ellipse cx="16" cy="16" rx="6" ry="10" fill="#fce7f3" opacity="0.5" transform="rotate(30 16 16)"/></svg>`,
  ];

  return svgs.map((svg) => {
    const img = document.createElement('img');
    img.src = `data:image/svg+xml;base64,${btoa(svg)}`;
    return img;
  });
}

function createLeafImages(): HTMLImageElement[] {
  const svgs = [
    // Maple leaf — orange
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M16 2 L20 10 L28 12 L22 18 L24 28 L16 24 L8 28 L10 18 L4 12 L12 10Z" fill="#f97316" opacity="0.6"/></svg>`,
    // Simple leaf — amber
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M16 2C12 2 6 10 6 18c0 4 4 8 10 12 6-4 10-8 10-12 0-8-6-16-10-16z" fill="#d97706" opacity="0.5"/></svg>`,
    // Small leaf — red
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M16 4C13 4 8 10 8 16c0 3 3 6 8 10 5-4 8-7 8-10 0-6-5-12-8-12z" fill="#dc2626" opacity="0.5"/></svg>`,
  ];

  return svgs.map((svg) => {
    const img = document.createElement('img');
    img.src = `data:image/svg+xml;base64,${btoa(svg)}`;
    return img;
  });
}

const SEASON_CONFIG: Record<Season, {
  images: () => HTMLImageElement[];
  count: number;
  speed: [number, number];
  wind: [number, number];
  radius: [number, number];
} | null> = {
  spring: {
    images: createPetalImages,
    count: 25,
    speed: [0.3, 1.0],
    wind: [-0.3, 0.8],
    radius: [6, 14],
  },
  autumn: {
    images: createLeafImages,
    count: 20,
    speed: [0.5, 1.5],
    wind: [-0.5, 1.0],
    radius: [8, 16],
  },
  winter: {
    images: () => [],
    count: 40,
    speed: [0.5, 2.0],
    wind: [-0.5, 0.5],
    radius: [1, 4],
  },
  summer: null,
};

export function SeasonalEffect() {
  const { data: settings } = useQuery({
    queryKey: ['companySettings'],
    queryFn: getCompanySettings,
    staleTime: 5 * 60 * 1000,
  });

  const season = getSeason();
  const config = SEASON_CONFIG[season];

  const images = useMemo(() => {
    if (!config || config.images().length === 0) return undefined;
    return config.images();
  }, [season]);

  if (!settings?.seasonalEffectsEnabled || !config) return null;

  return (
    <div
      inert={true}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <Snowfall
        style={{
          width: '100%',
          height: '100%',
        }}
        snowflakeCount={config.count}
        speed={config.speed}
        wind={config.wind}
        radius={config.radius}
        images={images}
      />
    </div>
  );
}
